var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { URLData } from '@telia-common-components/shared';
import { format, min } from 'date-fns';
import { action, computed, makeObservable, observable } from 'mobx';
import API from '../../../util/ApiUtil';
import ErrorUtil from '../../../util/ErrorUtil';
import { FetchStatus } from '../../../util/FetchStatus';
export const isMonthlyFee = (price) => (price === null || price === void 0 ? void 0 : price.type) === 'RECURRING' && (price === null || price === void 0 ? void 0 : price.periodType) === 'MONTH';
class Telia1Store {
    constructor(tier, app, lang, analytics, onSelectedBenefitsChange) {
        this.promotionEligibility = undefined;
        this.status = FetchStatus.UNINITIALIZED;
        this.selectedBenefitCodes = [];
        this.isEmployee = undefined;
        this.isEmployeeFetchStatus = FetchStatus.UNINITIALIZED;
        this.isActiveTelia1Client = undefined;
        this.fetchBenefits = async () => {
            if (this.status === FetchStatus.FETCHED) {
                return;
            }
            try {
                this.status = FetchStatus.FETCHING;
                const response = await this.api.get('/telia1/benefits');
                this.promotionEligibility = {
                    promotion: { name: 'VASPnM', promotionBenefitsMaxCount: 1, isActive: true },
                    promotionBenefits: response.data,
                    childPromotionEligibilities: []
                };
                this.status = FetchStatus.FETCHED;
            }
            catch (e) {
                this.status = FetchStatus.ERROR;
                ErrorUtil.pushErrorToTeliaRumAndLog(e);
            }
        };
        this.evaluatePromotion = async (saveStats) => {
            try {
                this.status = FetchStatus.FETCHING;
                const response = await this.api.get('/telia1/promotions/evaluate', {
                    params: { saveStats }
                });
                this.promotionEligibility = response.data;
                this.status = FetchStatus.FETCHED;
            }
            catch (e) {
                const error = e;
                if (error.response.status === 403) {
                    await this.fetchBenefits();
                }
                else {
                    this.status = FetchStatus.ERROR;
                    ErrorUtil.pushErrorToTeliaRumAndLog(error);
                }
            }
        };
        this.fetchIsEmployee = async (isLoggedIn) => {
            try {
                this.isEmployeeFetchStatus = FetchStatus.FETCHING;
                if (isLoggedIn) {
                    const response = await this.api.get('/mandate/is-employee', { timeout: 3000 });
                    this.isEmployee = response.data;
                }
                else {
                    this.isEmployee = false;
                }
                this.isEmployeeFetchStatus = FetchStatus.FETCHED;
            }
            catch (e) {
                this.isEmployeeFetchStatus = FetchStatus.ERROR;
                this.isEmployee = undefined;
                ErrorUtil.pushErrorToTeliaRumAndLog(e);
            }
        };
        this.fetchHasTelia1Products = async () => {
            try {
                const response = await this.twebApi.get('/product/has-telia1-products');
                this.isActiveTelia1Client = ['TELIA1_ELIGIBLE', 'TELIA1_ACTIVE'].every((item) => response.data.includes(item));
            }
            catch (e) {
                this.isActiveTelia1Client = undefined;
                ErrorUtil.pushErrorToTeliaRumAndLog(e);
            }
        };
        makeObservable(this);
        this.urlData = new URLData({ app: app, tier: tier, lang: lang });
        this.api = API(this.urlData.commonAPIBaseURL, { withCredentials: true });
        this.twebApi = API(`${this.urlData.publicBaseURL}/api`, { withCredentials: true });
        this.analytics = analytics;
        this.onSelectedBenefitsChange = onSelectedBenefitsChange;
    }
    get areBenefitsFetching() {
        return [FetchStatus.UNINITIALIZED, FetchStatus.FETCHING].includes(this.status);
    }
    get promotionEligibilitiesMaxBenefitsCountSum() {
        return this.promotionEligibilities
            .filter((promotionEligibility) => !!promotionEligibility.promotion.isActive)
            .reduce((acc, promotionEligibility) => acc + promotionEligibility.promotion.promotionBenefitsMaxCount, 0);
    }
    get promotionEligibilities() {
        return [
            { promotion: this.promotionEligibility.promotion, promotionBenefits: this.promotionEligibility.promotionBenefits },
            ...this.promotionEligibility.childPromotionEligibilities
        ];
    }
    get benefits() {
        return [...this.promotionEligibilities.flatMap((promotionEligibility) => [...promotionEligibility.promotionBenefits])];
    }
    get disabledChildren() {
        const replaceRootProductCategoryCodes = this.benefits
            .filter((benefit) => this.selectedBenefitCodes.includes(benefit.code) && benefit.metadata['type'] === 'replaceRootProduct')
            .flatMap((benefit) => benefit.metadata['substitutionParameters'].flatMap((parameters) => parameters.categoryCode));
        return this.benefits
            .filter((benefit) => {
            var _a;
            return !!((_a = benefit.eligibility) === null || _a === void 0 ? void 0 : _a.passed) &&
                benefit.metadata['type'] === 'discountChildProduct' &&
                replaceRootProductCategoryCodes.some((categoryCode) => categoryCode.includes(benefit.metadata['categoryCode']));
        })
            .map((benefit) => benefit.code);
    }
    get disabledReplaceParents() {
        const selectedCategoryCodes = this.benefits
            .filter((benefit) => this.selectedBenefitCodes.includes(benefit.code) && benefit.metadata['type'] === 'discountChildProduct')
            .flatMap((benefit) => benefit.metadata['categoryCode']);
        return this.benefits
            .filter((benefit) => {
            var _a;
            return !!((_a = benefit.eligibility) === null || _a === void 0 ? void 0 : _a.passed) &&
                benefit.metadata['type'] === 'replaceRootProduct' &&
                benefit.metadata['substitutionParameters']
                    .flatMap((parameters) => parameters.categoryCode)
                    .some((categoryCode) => selectedCategoryCodes.some((selectedCategoryCode) => categoryCode.includes(selectedCategoryCode)));
        })
            .map((benefit) => benefit.code);
    }
    get newBenefitsAvailableFrom() {
        const dates = this.promotionEligibility.promotionBenefits
            .flatMap((benefit) => benefit.eligibility.benefitAvailableFrom)
            .filter((date) => !!date)
            .map((date) => new Date(date));
        if (dates.length === 0) {
            return null;
        }
        const earliestBenefitEndDate = min(dates);
        const newBenefitsAvailableFrom = earliestBenefitEndDate.setDate(earliestBenefitEndDate.getDate() + 1);
        return format(newBenefitsAvailableFrom, 'dd.MM.yyyy');
    }
    get parentPromotionName() {
        return this.promotionEligibility.promotion.name;
    }
    availableBenefitsCount(promotionName) {
        return this.promotionMaxBenefits(promotionName) - this.effectiveBenefitsCount(promotionName);
    }
    get totalEffectiveBenefitsCount() {
        return this.benefits.filter((benefit) => benefit.isInEffect).length;
    }
    effectiveBenefitsCount(promotionName) {
        return this.promotionEligibilities
            .find((promotionEligibility) => promotionEligibility.promotion.name === promotionName)
            .promotionBenefits.filter((benefit) => benefit.isInEffect).length;
    }
    maxInEffect(promotionName) {
        return this.effectiveBenefitsCount(promotionName) >= this.promotionMaxBenefits(promotionName);
    }
    get anyPassedInTotal() {
        return this.benefits.some((benefit) => { var _a; return ((_a = benefit.eligibility) === null || _a === void 0 ? void 0 : _a.passed) && benefit.remainingDiscounts > 0; });
    }
    anyPassed(promotionName) {
        return this.promotionEligibilities
            .find((promotionEligibility) => promotionEligibility.promotion.name === promotionName)
            .promotionBenefits.some((benefit) => { var _a; return ((_a = benefit.eligibility) === null || _a === void 0 ? void 0 : _a.passed) && benefit.remainingDiscounts > 0; });
    }
    maxSelected(promotionName) {
        return this.countSelectedInPromotion(promotionName) >= this.availableBenefitsCount(promotionName);
    }
    toggleBenefit(checked, benefitCode) {
        this.analytics.push(checked ? `Choose Telia1 benefit - ${benefitCode}` : `Unchoose Telia1 benefit - ${benefitCode}`);
        checked ? this.selectedBenefitCodes.push(benefitCode) : this.selectedBenefitCodes.splice(this.selectedBenefitCodes.indexOf(benefitCode), 1);
        this.onSelectedBenefitsChange && this.onSelectedBenefitsChange(this.selectedBenefitCodes);
    }
    promotionMaxBenefits(promotionName) {
        return this.promotionEligibilities.find((promotionEligibility) => promotionEligibility.promotion.name === promotionName).promotion
            .promotionBenefitsMaxCount;
    }
    countSelectedInPromotion(promotionName) {
        return this.promotionEligibilities
            .find((promotionEligibility) => promotionEligibility.promotion.name === promotionName)
            .promotionBenefits.filter((benefit) => this.selectedBenefitCodes.includes(benefit.code)).length;
    }
}
__decorate([
    observable
], Telia1Store.prototype, "promotionEligibility", void 0);
__decorate([
    observable
], Telia1Store.prototype, "status", void 0);
__decorate([
    observable
], Telia1Store.prototype, "selectedBenefitCodes", void 0);
__decorate([
    observable
], Telia1Store.prototype, "urlData", void 0);
__decorate([
    observable
], Telia1Store.prototype, "isEmployee", void 0);
__decorate([
    observable
], Telia1Store.prototype, "isEmployeeFetchStatus", void 0);
__decorate([
    observable
], Telia1Store.prototype, "isActiveTelia1Client", void 0);
__decorate([
    computed
], Telia1Store.prototype, "areBenefitsFetching", null);
__decorate([
    computed
], Telia1Store.prototype, "promotionEligibilitiesMaxBenefitsCountSum", null);
__decorate([
    computed
], Telia1Store.prototype, "promotionEligibilities", null);
__decorate([
    computed
], Telia1Store.prototype, "benefits", null);
__decorate([
    computed
], Telia1Store.prototype, "disabledChildren", null);
__decorate([
    computed
], Telia1Store.prototype, "disabledReplaceParents", null);
__decorate([
    computed
], Telia1Store.prototype, "newBenefitsAvailableFrom", null);
__decorate([
    computed
], Telia1Store.prototype, "parentPromotionName", null);
__decorate([
    computed
], Telia1Store.prototype, "totalEffectiveBenefitsCount", null);
__decorate([
    computed
], Telia1Store.prototype, "anyPassedInTotal", null);
__decorate([
    action
], Telia1Store.prototype, "fetchIsEmployee", void 0);
export default Telia1Store;
